// components
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import Chart, { useChart } from 'src/components/chart';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { fPercent, formatNumber } from 'src/utils/format-number';
import { generateAnnotations } from 'src/utils/graph-demo-annotations';

// ----------------------------------------------------------------------

type Props = {
  series: {
    name: string;
    data: number[];
  }[];
  labels: string[]; // Ajout de la prop labels
  /** Override the per-series fill colors. Falls back to the legacy palette when omitted. */
  colors?: string[];
  /** Top radius applied to the last stacked segment of each column. */
  barBorderRadius?: number;
  /** Render the percentage of each segment on the bars. */
  showDataLabels?: boolean;
  /** Diameter (px) of the round legend markers. */
  legendMarkerSize?: number;
  /** Override the legend label text color. */
  legendLabelColor?: string;
  /** Chart render height (px). */
  height?: number;
  /** Hide the built-in ApexCharts legend (use when rendering a custom legend outside). */
  hideLegend?: boolean;
  /** Opt-in absolute-total rows appended below the per-series rows in the tooltip
   *  (e.g. Brand Traffic / Brand Searches). Rendered identically to the series rows
   *  (colored bar + name + value). `compute` receives the live per-series counts at
   *  the hovered point (same source as the per-series rows) and their names; its
   *  result is shown as a plain count (no percentage). Omitted => tooltip unchanged. */
  extraTooltipRows?: { name: string; color: string; compute: (values: number[], names: string[]) => number }[];
};

export default function ChartColumnStackedPercent({
  series,
  labels,
  colors,
  barBorderRadius = 0,
  showDataLabels = false,
  legendMarkerSize = 16,
  legendLabelColor,
  height = 350,
  hideLegend = false,
  extraTooltipRows,
}: Props) {
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace = useCurrentWorkspace()
  const annotations = generateAnnotations(workspace, filterStore, labels)

  enum ColorSchema {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
    TERTIARY = 'tertiary',
    ERROR = 'error',
    WARNING = 'warning',
    INFO = 'info',
    SUCCESS = 'success'
  }

  const resolvedColors =
    colors ??
    (series?.length === 3
      ? [theme.palette[ColorSchema.SUCCESS].light, theme.palette[ColorSchema.TERTIARY].light, '#b7b7b7']
      : series?.length === 5
        ? [theme.palette[ColorSchema.SUCCESS].light, '#1e88e5', '#64b5f6', '#b7dcfb', '#b7b7b7']
        : ['#118d57', '#1e88e5', '#64b5f6', '#E1E1E1']);

  const chartOptions = useChart({
    chart: {
      stacked: true,
      type: 'bar',
      stackType: '100%',
    },
    series: series,
    fill: {
      colors: resolvedColors,
    },
    colors: resolvedColors,
    labels: labels || [],
    // Give the x-axis date labels a little breathing room (gap above + below)
    // so they don't sit cramped against the bars / card edge.
    xaxis: {
      labels: { offsetY: 4 },
    },
    grid: {
      padding: { bottom: 8 },
    },
    legend: {
      show: !hideLegend,
      itemMargin: {
        vertical: 8,
      },
      markers: {
        customHTML: function () {
          return `<div style="width: ${legendMarkerSize}px; height: ${legendMarkerSize}px; border-radius: 50%; background-color: currentcolor;"></div>`;
        },
      },
      position: 'top',
      ...(legendLabelColor ? { labels: { colors: legendLabelColor } } : {}),
    },
    dataLabels: showDataLabels
      ? {
        enabled: true,
        formatter: (val: number, { seriesIndex, dataPointIndex, w }: any) => {
          const total = w.globals.stackedSeriesTotals[dataPointIndex];
          const value = w.globals.series[seriesIndex][dataPointIndex];
          if (!total || !value) return '';
          return `${((value / total) * 100).toFixed(0)}%`;
        },
        style: {
          fontSize: '11px',
          fontWeight: 600,
          fontFamily: 'Public Sans, sans-serif',
          colors: ['#1c252e'],
        },
        dropShadow: { enabled: false },
        background: { enabled: false },
      }
      : { enabled: false },
    plotOptions: {
      bar: {
        barHeight: '80%',
        columnWidth: '70%',
        borderRadius: barBorderRadius,
        distributed: false,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    stroke: {
      width: 0, // Désactiver les bordures des barres
      show: false,
    },
    yaxis: {
      labels: {
        formatter: (val: number) => {
          return `${val} %`;
        }
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function ({ series, dataPointIndex, w }) {
        if (!series || !w.globals.seriesNames || !w.globals.colors) {
          return '';
        }

        const date = w.globals.labels[dataPointIndex] || '';
        const total = w.globals.stackedSeriesTotals[dataPointIndex];
        const collapsed = [
          ...(w.globals.collapsedSeriesIndices || []),
          ...(w.globals.ancillaryCollapsedSeriesIndices || []),
        ];

        const rows = series.map((s: any, i: any) => {
          if (collapsed.includes(i)) return '';
          const value = s[dataPointIndex];
          const percentage = total ? ((value / total) * 100).toFixed(1) : 0;
          const formattedValue = value != null && !isNaN(value)
            ? `${formatNumber(value)} (${fPercent(percentage)})`
            : '0';
          return `
            <div style="display:flex;align-items:center;gap:8px;padding-left:4px;">
              <span style="display:inline-block;width:2px;height:12px;border-radius:2px;background-color:${w.globals.colors[i]};flex-shrink:0;"></span>
              <div style="flex:1;display:flex;justify-content:space-between;align-items:center;font-family:Public Sans,sans-serif;font-size:13px;font-weight:500;color:#333c47;white-space:nowrap;gap:16px;">
                <span>${w.globals.seriesNames[i]}</span>
                <span>${formattedValue}</span>
              </div>
            </div>
          `;
        }).join('');

        const pointValues = series.map((s: any) => Number(s?.[dataPointIndex]) || 0);
        const pointNames = w.globals.seriesNames || [];
        const extraRows = (extraTooltipRows && extraTooltipRows.length)
          ? extraTooltipRows.map((r) => {
              const v = r.compute(pointValues, pointNames);
              const fv = v != null && !isNaN(v) ? formatNumber(v) : '0';
              return `
                <div style="display:flex;align-items:center;gap:8px;padding-left:4px;">
                  <span style="display:inline-block;width:2px;height:12px;border-radius:2px;background-color:${r.color};flex-shrink:0;"></span>
                  <div style="flex:1;display:flex;justify-content:space-between;align-items:center;font-family:Public Sans,sans-serif;font-size:13px;font-weight:500;color:#333c47;white-space:nowrap;gap:16px;">
                    <span>${r.name}</span>
                    <span>${fv}</span>
                  </div>
                </div>
              `;
            }).join('')
          : '';

        return `
          <div style="background:#fcfcfd;border:1px solid rgba(42,50,59,0.2);border-radius:12px;box-shadow:1px 2px 5px rgba(0,0,0,0.06);overflow:hidden;min-width:240px;">
            <div style="padding:12px 12px 0 12px;">
              <p style="font-family:Public Sans,sans-serif;font-size:14px;font-weight:700;color:#333c47;white-space:nowrap;margin:0;">${date}</p>
            </div>
            <div style="padding:12px;display:flex;flex-direction:column;gap:6px;">
              ${rows}
              ${extraRows}
            </div>
          </div>
        `;
      },
      y: {
        formatter: (val: number, { dataPointIndex, w }) => {
          const total = w.globals.stackedSeriesTotals[dataPointIndex];
          const percentage = total ? ((val / total) * 100).toFixed(2) : 0;
          return `${val.toFixed(0)} (${percentage}%)`;
        },
      },
    },
    annotations: {
      xaxis: annotations
    },
  });

  return <Chart dir="ltr" type="bar" series={series} options={chartOptions} height={height} />;
}
